import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CalendarClock, CheckCircle2, ClipboardList, HandCoins, ListTodo, Users } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'wouter';
import { AttendanceCard } from '../components/AttendanceCard';
import { CompleteTaskModal } from '../components/ui/CompleteTaskModal';
import { EmptyState } from '../components/ui/EmptyState';
import { api } from '../lib/api';
import { useAuth, type Role } from '../lib/AuthContext';
import { useStaffList } from '../lib/options';
import { apiErrorMessage, useToast } from '../lib/ToastContext';
import { Loader } from '../components/ui/Spinner';

type Task = { id: string; taskNumber: string; title: string; description: string | null; dueAt: string; status: 'open' | 'completed' | 'overdue'; assignedTo: string | null; assignedBranchId: string | null };
const MANAGER_ROLES: Role[] = ['branch_head', 'region_head', 'general_head', 'hr', 'admin'];

type DashboardSummary = {
  headcount: number;
  attendanceToday: { present: number; late: number; halfDay: number; onLeave: number; absent: number; holiday: number; notMarked: number };
  branchComparison: { branchId: string; branchName: string; total: number; present: number; attendancePercent: number }[];
  pendingLeaveApprovals: number;
  pendingCorrections: number;
  openTasks: number;
  overdueTasks: number;
  loanOutstandingTotal: string | null;
  latestPayrollRun: { periodMonth: string; status: string; totalNetPay: string } | null;
};

function TeamOverviewSection() {
  const { user } = useAuth();
  const isPrivileged = user?.roles.some((r) => r === 'hr' || r === 'admin') ?? false;
  const summary = useQuery({ queryKey: ['dashboard-summary'], queryFn: async () => (await api.get<DashboardSummary>('/api/dashboard/summary')).data, refetchInterval: 60_000 });

  if (summary.isLoading) return <p className="text-muted">Loading team overview…</p>;
  if (!summary.data) return null;
  const d = summary.data;
  const attendancePercent = d.headcount > 0 ? Math.round(((d.attendanceToday.present + d.attendanceToday.late + d.attendanceToday.halfDay) / d.headcount) * 100) : 0;

  return (
    <section className="mb-8">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-xl font-bold">Team overview</h2>
        <Link href="/team" className="text-sm font-semibold text-brand-700">Live attendance →</Link>
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div className="card"><Users size={18} className="text-brand-600" /><p className="mt-2 text-2xl font-bold">{d.headcount}</p><p className="text-sm text-muted">Active staff</p></div>
        <div className="card"><CalendarClock size={18} className="text-brand-600" /><p className="mt-2 text-2xl font-bold">{attendancePercent}%</p><p className="text-sm text-muted">Present today</p></div>
        <div className="card"><ClipboardList size={18} className="text-brand-600" /><p className="mt-2 text-2xl font-bold">{d.pendingLeaveApprovals + d.pendingCorrections}</p><p className="text-sm text-muted">Pending approvals</p></div>
        <div className="card"><ListTodo size={18} className="text-brand-600" /><p className="mt-2 text-2xl font-bold">{d.openTasks}</p><p className="text-sm text-muted">Open tasks{d.overdueTasks > 0 ? ` (${d.overdueTasks} overdue)` : ''}</p></div>
        {isPrivileged && (
          <div className="card"><HandCoins size={18} className="text-brand-600" /><p className="mt-2 text-2xl font-bold">₹{Number(d.loanOutstandingTotal ?? '0').toLocaleString('en-IN')}</p><p className="text-sm text-muted">Loan balance outstanding</p></div>
        )}
        {isPrivileged && d.latestPayrollRun && (
          <div className="card">
            <CalendarClock size={18} className="text-brand-600" />
            <p className="mt-2 text-2xl font-bold capitalize">{d.latestPayrollRun.status}</p>
            <p className="text-sm text-muted">Latest payroll run ({new Date(`${d.latestPayrollRun.periodMonth}T00:00:00`).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })})</p>
          </div>
        )}
      </div>

      {d.branchComparison.length > 1 && (
        <div className="card mt-4 overflow-x-auto !p-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted">
                <th className="px-4 py-3 font-semibold">Branch</th>
                <th className="px-4 py-3 font-semibold">Present today</th>
                <th className="px-4 py-3 font-semibold">Attendance %</th>
              </tr>
            </thead>
            <tbody>
              {d.branchComparison.map((b) => (
                <tr key={b.branchId} className="border-b border-border last:border-0">
                  <td className="px-4 py-3"><strong>{b.branchName}</strong></td>
                  <td className="px-4 py-3 text-muted">{b.present} / {b.total}</td>
                  <td className="px-4 py-3"><span className={`badge ${b.attendancePercent >= 80 ? 'bg-green-50 text-green-700' : b.attendancePercent >= 50 ? 'bg-amber-50 text-amber-700' : 'bg-red-50 text-red-700'}`}>{b.attendancePercent}%</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function TasksSection() {
  const client = useQueryClient();
  const { user } = useAuth();
  const { success, error: toastError } = useToast();
  const isManager = user?.roles.some((r) => MANAGER_ROLES.includes(r)) ?? false;
  const tasks = useQuery({ queryKey: ['tasks'], queryFn: async () => (await api.get<Task[]>('/api/tasks')).data });
  const staff = useStaffList();
  const [completing, setCompleting] = useState<Task | null>(null);
  const complete = useMutation({
    mutationFn: ({ id, note }: { id: string; note: string }) => api.post(`/api/tasks/${id}/complete`, { note }),
    onSuccess: () => { void client.invalidateQueries({ queryKey: ['tasks'] }); success('Task marked complete'); setCompleting(null); },
    onError: (err) => toastError(apiErrorMessage(err, 'Could not complete task')),
  });
  const open = (tasks.data ?? []).filter((t) => t.status !== 'completed').sort((a, b) => a.dueAt.localeCompare(b.dueAt));
  const isOverdue = (t: Task) => new Date(t.dueAt) < new Date();
  const myId = user?.sub ?? user?.id;
  const assigneeLabel = (task: Task) => {
    if (task.assignedTo === myId) return null;
    if (task.assignedTo) return staff.data?.find((s) => s.id === task.assignedTo)?.name ?? 'Staff';
    if (task.assignedBranchId) return 'Whole branch';
    return null;
  };

  return (
    <section>
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-xl font-bold">{isManager ? 'Team tasks' : 'Your tasks'}</h2>
        <Link href="/tasks" className="text-sm font-semibold text-brand-700">View all →</Link>
      </div>
      {tasks.isLoading && <Loader />}
      {!tasks.isLoading && open.length === 0 && <EmptyState icon={CheckCircle2} title="Nothing pending" hint="You're all caught up." />}
      <div className="space-y-3">
        {open.slice(0, 6).map((task) => (
          <article key={task.id} className="card flex items-center justify-between gap-3">
            <Link href={`/tasks/${task.id}`} className="min-w-0 flex-1">
              <span className="font-mono text-xs font-semibold tracking-wide text-muted">{task.taskNumber}</span>
              <strong className="block truncate">{task.title}</strong>
              <p className={`mt-1 flex items-center gap-1.5 text-sm ${isOverdue(task) ? 'font-semibold text-red-700' : 'text-muted'}`}>
                {isOverdue(task) && <AlertTriangle size={14} />}
                Due {new Date(task.dueAt).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
              </p>
              {isManager && assigneeLabel(task) && <p className="mt-1 text-xs text-muted">Assigned to: {assigneeLabel(task)}</p>}
            </Link>
            <button className="btn-secondary shrink-0" onClick={() => setCompleting(task)}><CheckCircle2 size={18} /> Done</button>
          </article>
        ))}
      </div>
      {completing && (
        <CompleteTaskModal
          taskTitle={completing.title}
          isPending={complete.isPending}
          onConfirm={(note) => complete.mutate({ id: completing.id, note })}
          onClose={() => setCompleting(null)}
        />
      )}
    </section>
  );
}

export function DashboardPage() {
  const { user } = useAuth();
  const isManager = user?.roles.some((r) => MANAGER_ROLES.includes(r)) ?? false;

  return (
    <>
      <div className="mb-6">
        <p className="text-sm font-medium text-brand-700">{new Intl.DateTimeFormat('en-IN', { dateStyle: 'full' }).format(new Date())}</p>
        <h1 className="mt-1 text-3xl font-bold">Hello, {user?.name.split(' ')[0]}</h1>
      </div>

      {isManager && <TeamOverviewSection />}

      <div className="mb-8"><AttendanceCard /></div>

      <TasksSection />
    </>
  );
}
